/**
 * computedValues.ts — вычисляемые значения воронки из ответов роста и веса
 * (решение владельца №5, повтор Slimkit): переменные {{bmi}}, {{bmiLevel}},
 * {{normalWeightMin}}… для текстов блоков и числа для блоков, которым нужно
 * число (`bmi_scale` в режиме «из вычисления», будущие `profile_summary`,
 * `bmi_hint`, прогноз).
 *
 * ЧИСТЫЙ МОДУЛЬ (как `variable-context.ts`): без React и DOM, «сегодня» —
 * параметром.
 *
 * ОТКУДА ЕДИНИЦА. Пикеры уже пишут её в ответ (WEB-1301): число — под id
 * блока, единица — под служебным ключом `<id>::unit`, если посетитель
 * переключал; не переключал — единица блока по умолчанию
 * (`resolvePickerUnitDefault`). Обе половины берутся из тех же функций, что у
 * сводки ответов, — своей копии правила здесь нет.
 *
 * КАКОЙ ПИКЕР ЧЕЙ. Рост — первый `height_picker` воронки. Вес — по полю блока
 * `weightPickerRole`: `goal` — желаемый, `current` (или поле не задано) —
 * текущий; без явной роли желаемого нет (угадывать «второй пикер = цель» не
 * стали: в старых воронках это мог быть любой вес). Если контекст собран без
 * `measureSources` (старый вызов), источники выводятся из `blockMeta`:
 * первый рост и первый вес, желаемого нет.
 *
 * ЧЕСТНОСТЬ ЧИСЛА. Значение, выходящее за пределы эталона (рост 100–299 см /
 * 3–9 ft, вес 20–299 кг / 44–659 lb), не считается: переменная пустая, как
 * любая незаполненная (`resolveVariable` fail-open). Медицинское число лучше
 * не показать, чем показать абсурдное.
 */
import type { VariableContext } from './variable-context';
import { pickerUnitValueKey } from '@/components/quiz-runtime/answerCollection';
import {
  isComputedVariableName,
  type MeasureSource,
  type MeasureSources,
} from './measureSources';
import {
  pickBodyMetricsStrings,
  type BodyMetricsStrings,
} from './bodyMetricsStrings';
import {
  bmiCategory,
  bmiLevel,
  calcBmi,
  formatGoalDate,
  formatGoalMonthYear,
  goalDateParts,
  isHeightValid,
  isWeightValid,
  normalWeightRange,
  parseHeight,
  parseWeight,
  roundTo1,
  weightIn,
  weightLossPercent,
  type BmiCategory,
  type BmiLevel,
  type HeightUnit,
  type HeightValue,
  type WeightUnit,
  type WeightValue,
  WEIGHT_LIMITS,
} from './bodyMetrics';

/** Роль пикера веса. Порядок и значения сверяет сервер (computed-value-fields.ends.spec.ts). */
export const WEIGHT_PICKER_ROLES = ['current', 'goal'] as const;
export type WeightPickerRole = (typeof WEIGHT_PICKER_ROLES)[number];

/** Источник числа у шкалы ИМТ: ручной ввод (прежнее) или вычисление. */
export const BMI_VALUE_SOURCES = ['manual', 'computed'] as const;
export type BmiValueSource = (typeof BMI_VALUE_SOURCES)[number];

// WEB-3447: имена вычисляемых переменных и сбор источников роста и веса
// живут в лёгком `measureSources.ts` (он остаётся в первом экране); здесь —
// реэкспорт, чтобы прежние пути импорта не менялись.
export {
  COMPUTED_VARIABLE_NAMES,
  collectMeasureSources,
  isComputedVariableName,
  type ComputedVariableName,
  type MeasureSource,
  type MeasureSources,
} from './measureSources';

/** Запасной путь для контекста без `measureSources`: по описаниям блоков. */
function sourcesFromBlockMeta(ctx: VariableContext): MeasureSources {
  const out: MeasureSources = {};
  for (const [blockId, meta] of Object.entries(ctx.blockMeta ?? {})) {
    if (meta.type === 'height_picker' && !out.height) {
      out.height = { blockId, unitDefault: meta.unitDefault ?? 'cm' };
    } else if (meta.type === 'weight_picker' && !out.currentWeight) {
      out.currentWeight = { blockId, unitDefault: meta.unitDefault ?? 'kg' };
    }
  }
  return out;
}

function answerString(ctx: VariableContext, key: string): string | undefined {
  const v = ctx.answers[key];
  return typeof v === 'string' ? v : undefined;
}

function unitOf(ctx: VariableContext, source: MeasureSource): string {
  return (
    answerString(ctx, pickerUnitValueKey(source.blockId)) ?? source.unitDefault
  );
}

export interface BodyInputs {
  height: HeightValue | null;
  current: WeightValue | null;
  goal: WeightValue | null;
}

/** Ответы роста и веса с их единицами (без проверки пределов). */
export function readBodyInputs(ctx: VariableContext): BodyInputs {
  const sources = ctx.measureSources ?? sourcesFromBlockMeta(ctx);
  const height = (() => {
    if (!sources.height) return null;
    const unit = unitOf(ctx, sources.height);
    if (unit !== 'cm' && unit !== 'ft') return null;
    return parseHeight(
      answerString(ctx, sources.height.blockId),
      unit as HeightUnit,
    );
  })();
  const weight = (source: MeasureSource | undefined): WeightValue | null => {
    if (!source) return null;
    const unit = unitOf(ctx, source);
    if (unit !== 'kg' && unit !== 'lb') return null;
    return parseWeight(answerString(ctx, source.blockId), unit as WeightUnit);
  };
  return {
    height,
    current: weight(sources.currentWeight),
    goal: weight(sources.goalWeight),
  };
}

/** Посчитанные значения: числа и ключи, без текста. null — посчитать нельзя. */
export interface BodyValues {
  /** Единица показа веса: у текущего веса, иначе у желаемого, иначе по системе роста. */
  weightUnit: WeightUnit | null;
  bmi: number | null;
  bmiLevel: BmiLevel | null;
  bmiCategory: BmiCategory | null;
  normalWeightMin: number | null;
  normalWeightMax: number | null;
  currentWeight: number | null;
  goalWeight: number | null;
  weightLossPercent: number | null;
}

export function computeBodyValues(inputs: BodyInputs): BodyValues {
  const height =
    inputs.height && isHeightValid(inputs.height) ? inputs.height : null;
  const current =
    inputs.current && isWeightValid(inputs.current) ? inputs.current : null;
  const goal = inputs.goal && isWeightValid(inputs.goal) ? inputs.goal : null;
  const weightUnit: WeightUnit | null =
    current?.unit ??
    goal?.unit ??
    (height ? (height.unit === 'ft' ? 'lb' : 'kg') : null);
  const rawBmi = height && current ? calcBmi(height, current) : null;
  const bmi = rawBmi != null ? roundTo1(rawBmi) : null;
  const range =
    height && weightUnit ? normalWeightRange(height, weightUnit) : null;
  return {
    weightUnit,
    bmi,
    bmiLevel: bmi != null ? bmiLevel(bmi) : null,
    bmiCategory: rawBmi != null ? bmiCategory(rawBmi) : null,
    normalWeightMin: range?.min ?? null,
    normalWeightMax: range?.max ?? null,
    currentWeight:
      current && weightUnit ? Math.round(weightIn(current, weightUnit)) : null,
    // `chartV2/index.tsx:287`: `Math.round(gwRaw)` в единице показа.
    goalWeight:
      goal && weightUnit ? Math.round(weightIn(goal, weightUnit)) : null,
    weightLossPercent:
      current && goal ? weightLossPercent(current, goal) : null,
  };
}

/** Число на языке воронки: до одного знака после запятой, без разрядов. */
export function formatMetricNumber(
  value: number,
  locale: string | undefined,
): string {
  try {
    return new Intl.NumberFormat(locale || 'en', {
      maximumFractionDigits: 1,
      useGrouping: false,
    }).format(value);
  } catch {
    return String(value);
  }
}

function levelWord(level: BmiLevel, s: BodyMetricsStrings): string {
  return level === 'low'
    ? s.levelLow
    : level === 'high'
      ? s.levelHigh
      : s.levelNormal;
}

function categoryWord(category: BmiCategory, s: BodyMetricsStrings): string {
  switch (category) {
    case 'underweight':
      return s.categoryUnderweight;
    case 'overweight':
      return s.categoryOverweight;
    case 'obese':
      return s.categoryObese;
    default:
      return s.categoryNormal;
  }
}

/**
 * Значение вычисляемой переменной для текста. `undefined` — имя не
 * вычисляемое (разбор идёт дальше); `''` — вычисляемое, но посчитать нельзя.
 */
export function resolveComputedVariable(
  name: string,
  ctx: VariableContext,
  now: Date = new Date(),
): string | undefined {
  if (!isComputedVariableName(name)) return undefined;
  const locale = ctx.system.locale;
  const s = pickBodyMetricsStrings(locale);
  const v = computeBodyValues(readBodyInputs(ctx));
  const num = (n: number | null): string =>
    n == null ? '' : formatMetricNumber(n, locale);
  switch (name) {
    case 'bmi':
      return num(v.bmi);
    case 'bmiLevel':
      return v.bmiLevel ? levelWord(v.bmiLevel, s) : '';
    case 'bmiCategory':
      return v.bmiCategory ? categoryWord(v.bmiCategory, s) : '';
    case 'normalWeightMin':
      return num(v.normalWeightMin);
    case 'normalWeightMax':
      return num(v.normalWeightMax);
    case 'weightUnit':
      return v.weightUnit === 'lb'
        ? s.unitLb
        : v.weightUnit === 'kg'
          ? s.unitKg
          : '';
    case 'currentWeight':
      return num(v.currentWeight);
    case 'goalWeight':
      return num(v.goalWeight);
    case 'weightLossPercent':
      return num(v.weightLossPercent);
    case 'goalDate':
      // Дата цели осмысленна, только когда цель есть (у эталона — экран после
      // ввода желаемого веса).
      return v.goalWeight == null
        ? ''
        : formatGoalDate(goalDateParts(now), locale || 'en');
    case 'goalMonthYear':
      return v.goalWeight == null
        ? ''
        : formatGoalMonthYear(goalDateParts(now), locale || 'en');
    default:
      return '';
  }
}

/**
 * Значение шкалы ИМТ в режиме «из вычисления» — строкой, как поле
 * `bmiValue` (её разбирает `parseBmiValue`, запятую понимает). Пусто —
 * посчитать нельзя: ползунка нет, как у пустого ручного значения.
 */
export function computedBmiText(ctx: VariableContext | undefined): string {
  if (!ctx) return '';
  return resolveComputedVariable('bmi', ctx) ?? '';
}

// ── Подсказка под вводом роста/веса (spec/new-blocks/bmi-hint-card.md §4) ──

export type BmiHintStep = 'height' | 'current_weight' | 'goal_weight';
export type BmiHintTone = 'neutral' | 'warning' | 'success';
export type BmiHintState =
  | 'height_idle'
  | 'height_invalid'
  | 'weight_too_low'
  | 'weight_too_high'
  | 'bmi_underweight'
  | 'bmi_normal'
  | 'bmi_overweight'
  | 'bmi_obese'
  | 'goal_below_norm'
  | 'goal_not_less'
  | 'goal_ok';

export interface BmiHintDecision {
  state: BmiHintState;
  tone: BmiHintTone;
}

/**
 * Какое состояние подсказки показать (`warningBMIMessage/index.tsx:25-147`).
 * null — карточки нет (у эталона на шагах веса до ввода её нет, :70-72).
 * Шаги веса без известного роста: у эталона ИМТ тогда 0 («underweight 0»);
 * у нас карточки нет — показать ноль как диагноз хуже, чем промолчать.
 */
export function decideBmiHint(
  step: BmiHintStep,
  inputs: BodyInputs,
): BmiHintDecision | null {
  if (step === 'height') {
    // :26-33, 76-77, 130-131 — пусто или в пределах → «считаем», иначе ошибка.
    if (inputs.height && !isHeightValid(inputs.height)) {
      return { state: 'height_invalid', tone: 'warning' };
    }
    return { state: 'height_idle', tone: 'neutral' };
  }
  if (step === 'current_weight') {
    const w = inputs.current;
    if (!w) return null;
    if (!isWeightValid(w)) {
      // :41-52 — ниже нижней границы / выше верхней.
      const tooLow = w.value < WEIGHT_LIMITS[w.unit].min;
      return {
        state: tooLow ? 'weight_too_low' : 'weight_too_high',
        tone: 'warning',
      };
    }
    const bmi =
      inputs.height && isHeightValid(inputs.height)
        ? calcBmi(inputs.height, w)
        : null;
    if (bmi == null) return null;
    switch (bmiCategory(bmi)) {
      case 'underweight':
        return { state: 'bmi_underweight', tone: 'warning' };
      case 'normal':
        return { state: 'bmi_normal', tone: 'success' };
      case 'overweight':
        return { state: 'bmi_overweight', tone: 'warning' };
      default:
        return { state: 'bmi_obese', tone: 'warning' };
    }
  }
  // goal_weight — :88-127. Сравнения в единице желаемого веса.
  const goal = inputs.goal;
  if (!goal) return null;
  if (inputs.height && isHeightValid(inputs.height)) {
    const range = normalWeightRange(inputs.height, goal.unit);
    if (goal.value < range.min)
      return { state: 'goal_below_norm', tone: 'warning' };
  }
  const current = inputs.current ? weightIn(inputs.current, goal.unit) : 0;
  if (goal.value >= current) return { state: 'goal_not_less', tone: 'warning' };
  return { state: 'goal_ok', tone: 'neutral' };
}

const HINT_KEYS: Record<
  BmiHintState,
  [keyof BodyMetricsStrings, keyof BodyMetricsStrings | null]
> = {
  height_idle: ['hintHeightIdleTitle', 'hintHeightIdleText'],
  height_invalid: ['hintHeightInvalidTitle', null],
  weight_too_low: ['hintWeightTooLowTitle', null],
  weight_too_high: ['hintWeightTooHighTitle', null],
  bmi_underweight: ['hintUnderweightTitle', 'hintUnderweightText'],
  bmi_normal: ['hintNormalTitle', 'hintNormalText'],
  bmi_overweight: ['hintOverweightTitle', 'hintOverweightText'],
  bmi_obese: ['hintObeseTitle', 'hintObeseText'],
  goal_below_norm: ['hintGoalBelowNormTitle', 'hintGoalBelowNormText'],
  goal_not_less: ['hintGoalNotLessTitle', 'hintGoalNotLessText'],
  goal_ok: ['hintGoalOkTitle', 'hintGoalOkText'],
};

export interface BmiHintContent extends BmiHintDecision {
  /** Заголовок: разметка `<b>` эталона; подставленные значения — только числа. */
  titleHtml: string;
  textHtml: string | null;
}

/**
 * Готовая подсказка на языке воронки: состояние, тон и тексты с
 * подставленными {{bmi}}, {{weightLossPercent}}, {{normalWeightMin/Max}}.
 * Шаг «желаемый вес» считает границы нормы в единице ЖЕЛАЕМОГО веса.
 */
export function resolveBmiHint(
  step: BmiHintStep,
  ctx: VariableContext,
): BmiHintContent | null {
  const inputs = readBodyInputs(ctx);
  const decision = decideBmiHint(step, inputs);
  if (!decision) return null;
  const locale = ctx.system.locale;
  const s = pickBodyMetricsStrings(locale);
  const rawBmi =
    inputs.height && inputs.current
      ? calcBmi(inputs.height, inputs.current)
      : null;
  const range =
    inputs.height && inputs.goal && isHeightValid(inputs.height)
      ? normalWeightRange(inputs.height, inputs.goal.unit)
      : null;
  const pct =
    inputs.current && inputs.goal
      ? weightLossPercent(inputs.current, inputs.goal)
      : null;
  const values: Record<string, string> = {
    bmi: rawBmi != null ? formatMetricNumber(roundTo1(rawBmi), locale) : '',
    weightLossPercent: pct != null ? formatMetricNumber(pct, locale) : '',
    normalWeightMin: range ? formatMetricNumber(range.min, locale) : '',
    normalWeightMax: range ? formatMetricNumber(range.max, locale) : '',
  };
  const fill = (template: string): string =>
    template.replace(
      /\{\{\s*([a-zA-Z]+)\s*\}\}/g,
      (_, key: string) => values[key] ?? '',
    );
  const [titleKey, textKey] = HINT_KEYS[decision.state];
  return {
    ...decision,
    titleHtml: fill(s[titleKey] as string),
    textHtml: textKey ? fill(s[textKey] as string) : null,
  };
}
